import { useCreateCheckoutSession, useGetMyProfile, useCreatePortalSession } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUser } from "@clerk/react";
import { Check, Crown, Mail, Star, Zap } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { PRICING_PAGE_SEO } from "@/seo-metadata";

const FREE_FEATURES = [
  "瀏覽全部招標標題",
  "查看招標地區及分類",
  "查看截止日期",
  "基本搜尋及篩選功能",
  "電郵通知基本招標資訊",
];

const PAID_FEATURES = [
  "查看學校名稱",
  "查看聯絡方式及招標詳情",
  "每日電郵通知最新招標詳情",
  "Telegram 群組接收最新招標詳情",
  "加入付費會員 WhatsApp 社群",
  "自定收藏標案到我的最愛",
  "免費功能全部包含",
];

function CheckoutButton({ plan }: { plan: "monthly" | "yearly" | "test" }) {
  const createCheckout = useCreateCheckoutSession();
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const domain = window.location.origin;
  const interval = plan === "yearly" ? "year" : plan === "test" ? "test" : "month";

  const handleCheckout = () => {
    createCheckout.mutate({
      data: {
        successUrl: `${domain}${basePath}/dashboard?success=1&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${domain}${basePath}/pricing`,
        interval,
      } as any
    }, {
      onSuccess: (data: any) => {
        if (data.url) window.location.href = data.url;
      },
      onError: () => {
        alert("付款頁面載入失敗，請稍後再試或聯絡 cs@skoltend.com");
      },
    });
  };

  if (plan === "test") {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed border-amber-400 text-amber-700 hover:bg-amber-50"
          onClick={handleCheckout}
          disabled={createCheckout.isPending}
        >
          {createCheckout.isPending ? "處理中..." : "[測試] HK$10 / 3日訂閱"}
        </Button>
        {createCheckout.isError && (
          <p className="text-xs text-destructive mt-1 text-center">載入失敗，請稍後再試</p>
        )}
      </>
    );
  }

  return (
    <>
      <Button
        className="w-full"
        onClick={handleCheckout}
        disabled={createCheckout.isPending}
        data-testid={`button-checkout-${plan}`}
      >
        {createCheckout.isPending ? "處理中..." : plan === "yearly" ? "訂閱年費計劃" : "訂閱月費計劃"}
      </Button>
      {createCheckout.isError && (
        <p className="text-xs text-destructive mt-2 text-center">
          付款頁面載入失敗，請稍後再試
        </p>
      )}
    </>
  );
}

function ManageSubscriptionButton() {
  const createPortal = useCreatePortalSession();
  const handlePortal = () => {
    createPortal.mutate(undefined as any, {
      onSuccess: (data) => {
        if (data.url) window.location.href = data.url;
      }
    });
  };
  return (
    <Button variant="outline" onClick={handlePortal} disabled={createPortal.isPending} data-testid="button-manage-subscription">
      {createPortal.isPending ? "載入中..." : "管理訂閱"}
    </Button>
  );
}

function PaidMemberSection() {
  const { data: profile } = useGetMyProfile();
  if (profile?.membershipTier === "paid") {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full font-semibold mb-4">
          <Crown className="h-5 w-5" />
          您已是付費會員
        </div>
        <p className="text-muted-foreground mb-4">感謝您的支持！您可以查看所有招標詳情並使用全部功能。</p>
        <ManageSubscriptionButton />
      </div>
    );
  }
  return null;
}

export default function PricingPage() {
  const { isSignedIn, isLoaded } = useUser();
  const signedIn = isLoaded && !!isSignedIn;
  const signedOut = !signedIn;

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={PRICING_PAGE_SEO.shortTitle}
        description={PRICING_PAGE_SEO.description}
        keywords={PRICING_PAGE_SEO.keywords}
        canonical="/pricing"
      />
      <div className="bg-secondary/30 border-b py-12">
        <div className="container mx-auto px-4 md:px-8 text-center max-w-2xl">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">訂閱計劃</Badge>
          <h1 className="text-3xl font-bold mb-3">選擇適合您的計劃</h1>
          <p className="text-muted-foreground">
            免費瀏覽招標標題，付費會員解鎖全部資訊及通知功能
          </p>
        </div>
      </div>
      <div className="container mx-auto px-4 md:px-8 py-12 max-w-4xl">
        {signedIn && <PaidMemberSection />}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Free Plan */}
          <Card className="relative">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between mb-2">
                <CardTitle className="text-lg">免費計劃</CardTitle>
                <Badge variant="secondary">免費</Badge>
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold">$0</span>
                <span className="text-muted-foreground text-sm ml-2">/ 年</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {FREE_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              {signedOut ? (
                <Button variant="outline" className="w-full" asChild>
                  <a href="/sign-up" data-testid="button-free-signup">免費註冊</a>
                </Button>
              ) : (
                <Button variant="outline" className="w-full" disabled>目前計劃</Button>
              )}
            </CardContent>
          </Card>

          {/* Paid Plan */}
          <Card className="relative border-primary/40 shadow-md">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-primary text-primary-foreground px-3 py-1">
                <Zap className="h-3 w-3 mr-1" />
                推薦計劃
              </Badge>
            </div>
            <CardHeader className="pb-4 pt-7">
              <div className="flex items-center justify-between mb-2">
                <CardTitle className="text-lg">付費會員</CardTitle>
                <Badge className="bg-primary/10 text-primary border-primary/20">無限查閱</Badge>
              </div>
              <div className="mt-2">
                <span className="text-3xl font-bold text-primary">HK$5,888</span>
                <span className="text-muted-foreground text-sm ml-2">/ 年</span>
              </div>
              <p className="text-xs text-muted-foreground">平均每月 HK$490.7</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {PAID_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-3 text-sm">
                    {f.includes("Telegram") ? (
                      <img src="/telegram-logo.webp" alt="" width={16} height={16} className="h-4 w-4 shrink-0" />
                    ) : f.includes("WhatsApp") ? (
                      <img src="/whatsapp-logo.webp" alt="" width={16} height={16} className="h-4 w-4 shrink-0" />
                    ) : f.includes("每日電郵") ? (
                      <Mail className="h-4 w-4 text-blue-500 shrink-0" />
                    ) : f.includes("我的最愛") ? (
                      <Star className="h-4 w-4 text-amber-400 shrink-0" />
                    ) : (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                    {f}
                  </li>
                ))}
              </ul>
              {signedOut ? (
                <Button className="w-full" asChild>
                  <a href="/sign-up" data-testid="button-paid-signup">立即開始</a>
                </Button>
              ) : (
                <CheckoutButton plan="yearly" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground text-sm">
            如有疑問，請聯絡我們：
            <a href="mailto:cs@skoltend.com" className="text-primary hover:underline ml-1">cs@skoltend.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
